import React from "react";
import { Icon } from "../../UIElements/Icon";
import { useGlobalUtils } from "../../../../lib/globalUtils";
import { useTranslation } from "react-i18next";
import {
  STATUS_LABELS,
  STATUS_BADGE_COLORS,
  CHANNEL_ICONS,
  CHANNEL_COLORS,
  getStatusGroup,
  getCustomerName,
  getCustomerPhone,
} from "@finitless/shared/orderConstants";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  HoverCardArrow,
} from "../../UIElements/HoverCard";

const OrderCard = ({
  order,
  onViewDetails,
  onViewConversation,
  statusLabels,
  getStatusGroup: getStatusGroupProp,
}) => {
  const { t } = useTranslation("orders");
  const { parsePrice, calculateEstimatedDelivery, parseDate } =
    useGlobalUtils();

  const getStatusBadge = (status) => {
    const statusGroup = getStatusGroupProp
      ? getStatusGroupProp(status)
      : getStatusGroup(status);
    const fallbackStatusLabel = statusLabels
      ? statusLabels[status]
      : STATUS_LABELS[status] || status;
    const statusLabel = t(`statusLabels.${status}`, {
      defaultValue: fallbackStatusLabel,
    });

    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
          STATUS_BADGE_COLORS[statusGroup] || STATUS_BADGE_COLORS.unknown
        }`}
        title={statusLabel}
      >
        <span className="block max-w-[140px] truncate">{statusLabel}</span>
      </span>
    );
  };

  const ChannelIconName = CHANNEL_ICONS[order.channel] || "MessageSquare";
  const orderTime = parseDate(order.created_at, { useUTC: true });
  const estimatedDeliveryTime = parseDate(calculateEstimatedDelivery(order));
  const showEstimatedDelivery = estimatedDeliveryTime !== orderTime;

  return (
    <div className="flex h-full flex-col rounded-lg border border-gray-800 bg-gray-900 p-3 transition-colors hover:bg-gray-800/50 sm:p-4">
      {/* Header */}
      <div className="mb-3">
        <div className="flex items-start space-x-2">
          <div
            className={`rounded-lg bg-gray-800 p-1 sm:p-1.5 ${
              CHANNEL_COLORS[order.channel] || "text-gray-400"
            } flex-shrink-0`}
          >
            <Icon name={ChannelIconName} className="h-3 w-3 sm:h-4 sm:w-4" />
          </div>
          <div className="min-w-0 flex-1">
            {/* Order Title */}
            <h3 className="mb-1.5 text-xs font-semibold text-white sm:text-sm">
              {t("card.orderNumber", { id: order.id })}
            </h3>
            {/* Date and Status */}
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-0.5 text-xs text-gray-400">
                <p>
                  {t("detail.labels.orderTime")}: {orderTime}
                </p>
                {showEstimatedDelivery && (
                  <p>
                    {t("detail.labels.estimatedDelivery")}:{" "}
                    {estimatedDeliveryTime}
                  </p>
                )}
              </div>
              <div className="flex-shrink-0">
                {getStatusBadge(order.status)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Business Info */}
      {order.business && (
        <div className="mb-2.5">
          <div className="flex items-center space-x-2">
            {order.business.logo ? (
              <img
                src={order.business.logo}
                alt={order.business.name || t("card.business")}
                className="h-5 w-5 flex-shrink-0 rounded object-cover"
              />
            ) : (
              <Icon
                name="Store"
                className="h-5 w-5 flex-shrink-0 text-gray-400"
              />
            )}
            <div className="min-w-0 flex-1">
              <span className="block truncate text-xs font-medium text-white sm:text-sm">
                {order.business.name || t("common.notAvailable")}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Customer Info */}
      <div className="mb-3">
        <div className="flex items-start space-x-2">
          <Icon name="User" className="h-5 w-5 flex-shrink-0 text-gray-400" />
          <div className="min-w-0 flex-1">
            <span className="block truncate text-xs font-medium text-white sm:text-sm">
              {getCustomerName(order)}
            </span>
            <span className="block truncate text-xs text-gray-400">
              {getCustomerPhone(order)}
            </span>
            <div className="mt-0.5 flex items-start space-x-1.5">
              <Icon
                name="MapPin"
                className="mt-0.5 h-3 w-3 flex-shrink-0 text-gray-500"
              />
              <span className="line-clamp-1 text-xs text-gray-400">
                {order?.customer?.address || t("common.notAvailable")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Order Items - Fixed height with scroll */}
      <div className="mb-3 min-h-0 flex-1">
        <div className="max-h-24 space-y-1 overflow-y-auto sm:max-h-32 sm:space-y-1.5">
          {(order.products || []).map((product, index) => (
            <div
              key={product.id || index}
              className="rounded-lg bg-gray-800 p-1.5 sm:p-2"
            >
              {/* Product Header */}
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <h4 className="truncate text-xs font-medium text-white">
                    {t("detail.labels.productWithQuantity", {
                      quantity: product.quantity,
                      name: product.name,
                    })}
                  </h4>
                </div>
                <div className="ml-2 flex-shrink-0 text-right">
                  <div className="text-xs font-medium text-white">
                    {parsePrice(product?.total || product?.summary?.total) ||
                      "0.00"}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer - Always at bottom */}
      <div className="mt-auto">
        {/* Total */}
        <div className="mb-2 border-t border-gray-700 pt-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-white sm:text-sm">
              {t("card.total")}
            </span>
            <span className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-sm font-bold text-transparent sm:text-base">
              {parsePrice(order.summary?.total || 0)}
            </span>
          </div>
        </div>

        {/* Instructions */}
        {order.instructions && (
          <div className="mb-2 rounded-lg bg-gray-800 p-1.5 sm:p-2">
            <p className="line-clamp-1 text-xs text-gray-300">
              <strong>{t("card.instructions")}:</strong> {order.instructions}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2 pt-1">
          <button
            onClick={() => onViewDetails(order)}
            className="flex h-8 items-center justify-center gap-1.5 rounded-md border border-gray-600 bg-gray-800 text-xs font-medium text-gray-200 transition-colors hover:border-gray-500 hover:bg-gray-700 hover:text-white"
          >
            <Icon name="FileText" className="h-3.5 w-3.5" />
            {t("card.actions.details")}
          </button>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button
                onClick={
                  order?.conversation_id
                    ? () => onViewConversation(order)
                    : null
                }
                className={`flex h-8 items-center justify-center gap-1.5 rounded-md border text-xs font-medium transition-colors ${
                  order?.conversation_id
                    ? "border-blue-500/40 bg-blue-600 text-white hover:bg-blue-500"
                    : "cursor-not-allowed border-gray-700 bg-gray-800 text-gray-500"
                }`}
                disabled={!order?.conversation_id}
              >
                <Icon
                  name="MessageCircle"
                  className={`h-3.5 w-3.5 ${
                    order?.conversation_id ? "text-white" : "text-gray-500"
                  }`}
                />
                {t("card.actions.chat")}
              </button>
            </HoverCardTrigger>
            {!order?.conversation_id && (
              <HoverCardContent side="top" align="end" className="w-64">
                <HoverCardArrow />
                <div className="flex items-start space-x-2">
                  <Icon
                    name="Info"
                    className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-400"
                  />
                  <div>
                    <p className="mb-1 text-sm font-medium text-white">
                      {t("card.chatUnavailable.title")}
                    </p>
                    <p className="text-xs text-gray-400">
                      {t("card.chatUnavailable.description")}
                    </p>
                  </div>
                </div>
              </HoverCardContent>
            )}
          </HoverCard>
        </div>
      </div>
    </div>
  );
};

export default OrderCard;
